import React from 'react'
import styled from '@emotion/styled'

import {BottomSheet} from '@vio/atlas/BottomSheet'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {PopUp} from '@vio/atlas/PopUp'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {hasChargeLaterPayment} from '@vio/core/src/offer/business/offerPaymentMode'
import {Offer} from '@vio/core/src/offer/types/offer'
import {RoomConfigurationType} from '@vio/core/src/room/types/RoomConfiguration'
import {calculateDateDifference} from '@vio/core/src/utils/date'

import {PriceDetails} from '../PriceDetails'
import {RoomDetailsSection} from '../RoomDetailsSection'
export interface PriceBreakdownModalProps {
  checkIn: string
  checkOut: string
  roomsConfig: RoomConfigurationType
  roomName: string
  offer: Offer
  isOpen: boolean
  onClose: () => void
  prefersReducedMotion?: boolean
  hasAdditionalCharges?: boolean
  hotelCountryCode?: string
  isLocalVatIncluded?: boolean
  showVatDisclaimer?: boolean
}

const RoomDetailsWrapper = styled.div`
  padding: 0 0 ${cssTheme.layout.spacing.s100};
`

const DesktopContentWrapper = styled.div`
  width: 568px;
`

const Title = () => (
  <Text variant="titleM">
    <FormattedMessageWrapper
      id="priceBreakdown"
      defaultMessage="Price Breakdown"
    />
  </Text>
)

export const PriceBreakdownModal = ({
  checkIn,
  checkOut,
  roomsConfig,
  roomName,
  offer,
  isOpen = false,
  onClose,
  prefersReducedMotion,
  hasAdditionalCharges = false,
  hotelCountryCode,
  isLocalVatIncluded,
  showVatDisclaimer
}: PriceBreakdownModalProps) => {
  const {isMobile} = useDeviceLayout()
  const numberOfNights = calculateDateDifference(
    new Date(checkIn),
    new Date(checkOut)
  )

  // Keep modal mounted to ensure body scroll lock works properly
  const shouldRenderContent = Boolean(roomsConfig) && Boolean(offer)
  const isOfferChargeLaterEligible = offer
    ? hasChargeLaterPayment(offer)
    : false

  const Content = () =>
    shouldRenderContent ? (
      <div data-id="priceBreakdownComponent">
        <RoomDetailsWrapper>
          <RoomDetailsSection
            showRoomDetails
            mealPlan={offer.mealPlan}
            numberOfNights={numberOfNights}
            numberOfRooms={roomsConfig.numberOfRooms}
            roomName={roomName}
            cancellationPenalties={offer.cancellationPenalties}
            offerPrices={offer.prices}
            paymentMode={offer.paymentMode}
            includeNumberOfRooms
            showBasePrice
            isLoading={!offer}
            isChargeLaterEligible={isOfferChargeLaterEligible}
          />
        </RoomDetailsWrapper>
        <PriceDetails
          showFeesDescriptions={false}
          checkIn={checkIn}
          checkOut={checkOut}
          numberOfGuests={
            roomsConfig.numberOfAdults + roomsConfig.numberOfChildren
          }
          numberOfRooms={roomsConfig.numberOfRooms}
          paymentMode={offer.paymentMode}
          prices={offer.prices}
          hasAdditionalCharges={hasAdditionalCharges}
          hotelCountryCode={hotelCountryCode}
          isLocalVatIncluded={isLocalVatIncluded}
          showVatDisclaimer={showVatDisclaimer}
        />
      </div>
    ) : null

  return (
    <>
      {isMobile ? (
        <BottomSheet
          isOpen={isOpen}
          navBarProps={{centerContent: <Title />}}
          onClose={onClose}
          prefersReducedMotion={prefersReducedMotion}
        >
          <Content />
        </BottomSheet>
      ) : (
        <PopUp
          width={600}
          isOpen={isOpen}
          navBarProps={{centerContent: <Title />}}
          closeAction={onClose}
        >
          <DesktopContentWrapper>
            <Content />
          </DesktopContentWrapper>
        </PopUp>
      )}
    </>
  )
}
